import React, { useMemo, useState } from "react";
import { ToastContext } from "@/components/ToastProvider.jsx";

export default function ProductCard({ p, onClick, showAdd = false, onAdd, showQuick = false, onQuick, showStock = true, userLoggedIn = true }) {
  const getFirstImage = (prod) => {
    if (typeof prod?.imageUrl === "string" && prod.imageUrl.trim()) return prod.imageUrl.trim();
    if (typeof prod?.thumbnail === "string" && prod.thumbnail.trim()) return prod.thumbnail.trim();
    if (Array.isArray(prod?.images) && prod.images.length && typeof prod.images[0] === "string") {
      return prod.images[0].trim();
    }
    return "";
  };
  const [imgOk, setImgOk] = useState(true);
  const img = getFirstImage(p);
  const createdAtMs = useMemo(() => {
    const ts = p?.createdAt;
    if (!ts) return null;
    if (typeof ts === "number") return ts * 1000;
    if (typeof ts?.toMillis === "function") return ts.toMillis();
    if (typeof ts?.seconds === "number") return ts.seconds * 1000;
    if (typeof ts?._seconds === "number") return ts._seconds * 1000;
    return null;
  }, [p]);
  const isNew = useMemo(() => {
    if (!createdAtMs) return false;
    const ageDays = (Date.now() - createdAtMs) / (1000 * 60 * 60 * 24);
    return ageDays <= 14; // new within 14 days
  }, [createdAtMs]);
  const stockNum = Number(p?.stock ?? 0);
  const isOut = stockNum <= 0;
  const isLow = stockNum > 0 && stockNum <= 5;
  const price = Number(p?.price || 0);
  const compareAt = Number(p?.compareAtPrice || 0);
  const onSale = compareAt > price;
  // Use the product's quantityStep field, fallback to 0.25 for backward compatibility
      const step = Number(p?.quantityStep) || 0.25;
    
    // Initialize with empty string to prevent auto-filling
    const [qty, setQty] = useState('');
  
  const snapQuarter = (n) => Math.round(Number(n || 0) * 4) / 4;
  const { notify } = React.useContext(ToastContext);
  const [cooling, setCooling] = useState(false);

  // Handle share functionality
  const handleShare = async () => {
    const shareData = {
      title: p.name,
      text: `Check out ${p.name} - ₱${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
      url: window.location.href
    };

    try {
      if (navigator.share && navigator.canShare(shareData)) {
        await navigator.share(shareData);
      } else {
        // Fallback: copy to clipboard
        const shareText = `${p.name} - ₱${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - ${window.location.href}`;
        await navigator.clipboard.writeText(shareText);
        notify('Product link copied to clipboard!', { type: 'success', duration: 2000 });
      }
    } catch (error) {
      
      // Fallback: copy to clipboard
      try {
        const shareText = `${p.name} - ₱${price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} - ${window.location.href}`;
        await navigator.clipboard.writeText(shareText);
        notify('Product link copied to clipboard!', { type: 'success', duration: 2000 });
      } catch (clipboardError) {
        notify('Failed to share product', { type: 'error', duration: 2000 });
      }
    }
  };

  function sanitizeNumericDot(v) {
    if (typeof v !== "string") v = String(v ?? "");
    let s = v.replace(/[^\d.]/g, "");
    // Ensure only one dot
    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }
    // Limit to two decimals
    const parts = s.split(".");
    if (parts.length > 1) s = parts[0] + "." + parts[1].slice(0, 2);
    else s = parts[0];
    return s;
  }

  return (
    <div 
      className="group rounded-xl border bg-white hover:shadow transition cursor-pointer relative"
      onClick={onClick}
    >
      <div className="relative aspect-square w-full overflow-hidden rounded-t-xl bg-gray-50">
        {img && imgOk ? (
          <img
            src={img}
            alt={p.name}
            className="w-full h-full object-cover"
            loading="lazy" decoding="async" width="400" height="400"
            onError={() => setImgOk(false)}
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-ink/40">No Image</div>
        )}
        {/* Badges */}
        {showStock && (
          <div className="absolute top-2 left-2 space-y-1">
            {isOut && (
              <div className="bg-red-600 text-white text-[11px] px-2 py-0.5 rounded">Out of stock</div>
            )}
            {!isOut && isLow && (
              <div className="bg-amber-500 text-white text-[11px] px-2 py-0.5 rounded">Low stock</div>
            )}
            {!isOut && isNew && (
              <div className="bg-emerald-600 text-white text-[11px] px-2 py-0.5 rounded">New</div>
            )}
            {!isOut && onSale && (
              <div className="bg-rose-600 text-white text-[11px] px-2 py-0.5 rounded">Sale</div>
            )}
          </div>
        )}
        {showQuick && (
          <div className="absolute top-2 right-2">
            <button
              type="button"
              className="btn btn-sm btn-outline opacity-0 group-hover:opacity-100 transition"
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); onQuick && onQuick(p); }}
            >
              Quick view
            </button>
          </div>
        )}
      </div>
      <div className="p-2 sm:p-3">
        <div className="flex items-start justify-between gap-2">
          <div className="font-medium line-clamp-2 text-sm sm:text-base leading-tight flex-1">{p.name}</div>
          {/* Share Button */}
          <button
            type="button"
            className="btn btn-xs btn-ghost transition-all duration-200 p-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleShare();
            }}
            title="Share product"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <polyline points="16,6 12,2 8,6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <line x1="12" y1="2" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        
        {/* Rating and Review Count */}
        <div className="mt-1 flex items-center gap-2 text-xs text-ink/60">
          <div className="flex items-center gap-1">
            {/* Star Rating - Always yellow for now */}
            {[1, 2, 3, 4, 5].map((star) => (
              <svg
                key={star}
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="currentColor"
                className="text-yellow-400"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" stroke="currentColor" strokeWidth="1.5"/>
              </svg>
            ))}
            <span className="ml-1 font-medium">{p.rating || 4.5}</span>
          </div>
          <span className="text-ink/40">({p.reviewCount || 8} reviews)</span>
        </div>
        
        <div className="mt-1 font-mono flex items-center gap-2 text-sm">
          <span>
            ₱{price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
          {onSale && (
            <span className="text-ink/50 line-through text-xs">
              ₱{compareAt.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
        </div>
        {showStock && (
          <div className="mt-1 text-xs text-ink/60">Stock: {stockNum}</div>
        )}
        {showAdd && (
          <div className="mt-2 flex flex-col sm:flex-row items-start sm:items-center gap-2">
            {/* qty input */}
            <div className="inline-flex items-center gap-1 w-full sm:w-auto">
              <input
                type="number"
                className="input h-10 sm:h-8 w-full sm:w-24 text-center text-sm"
                min={step}
                step={step}
                value={qty}
                onClick={(e) => e.stopPropagation()}
                                  onChange={(e) => {
                    // Allow only digits and dot while typing
                    const s = sanitizeNumericDot(e.target.value);
                    setQty(s);
                  }}
                onBlur={(e) => {
                  const n = Number(e.target.value);
                  const min = step;
                  
                  // Allow 0 as a valid input
                  if (isNaN(n) || n < 0) {
                    setQty('');
                  } else if (n > 0 && n < min) {
                    setQty(min);
                  } else if (stockNum != null && n > stockNum) {
                    setQty(Math.max(min, stockNum));
                  } else {
                    setQty(n);
                  }
                }}
                disabled={isOut}
                aria-label="Quantity"
              />
            </div>
            <button
              className={`btn btn-sm w-full sm:w-9 h-10 sm:h-9 p-0 grid place-items-center text-sm ${
                userLoggedIn ? 'btn-primary' : 'btn-outline'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                
                if (!userLoggedIn) {
                  // Show sign-in prompt for non-logged-in users
                  notify('Please sign in to add items to cart', { type: 'info', duration: 3000 });
                  return;
                }
                
                // Use the same logic as ProductDetail - explicitly convert to number
                const raw = Number(qty || step);
                const want = Math.max(step, step === 0.25 ? snapQuarter(raw) : Math.round(raw / step) * step);
                if (onAdd) {
                  onAdd({ ...p, qty: want });
                }
                setCooling(true);
                setTimeout(() => setCooling(false), 500);
              }}
              disabled={isOut || cooling}
              title={isOut ? "Out of stock" : (cooling ? "Please wait…" : (userLoggedIn ? "Add to cart" : "Sign in to add to cart"))}
              aria-label={isOut ? "Out of stock" : (cooling ? "Please wait…" : (userLoggedIn ? "Add to cart" : "Sign in to add to cart"))}
            >
              {/* shopping cart icon */}
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M7 4h-2l-1 2m0 0l3 9h9l3-7H6M4 6h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="9" cy="20" r="1.5" fill="currentColor"/>
                <circle cx="17" cy="20" r="1.5" fill="currentColor"/>
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
